import { memo, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  TextField,
} from "@mui/material";
import { Close as CloseIcon } from "components/icons";
import { handleAxiosError } from "app/services/app.error.handler";
import { parseApiError } from "app/services/api.error.parser";
import { loadAllClients } from "app/services/client.service";
import { useDispatch, useSelector } from "react-redux";
import MDBox from "components/MDBox";
import MasterDataSelect from "app/components/shared/MasterDataSelect";
import { clientActions } from "store/client-slice";
import { validateClientProject } from "utils/clientProjectValidationUtil";
import { ConfirmDialog } from "../ConfirmDialog";
import {
  addClientProjectToBackend,
  updateClientProjectToBackend,
} from "app/services/client-project.service";
import * as AppConstants from "AppConstants";
import { trimOnBlur, trimTextFields } from "utils/trimOnBlur";

/**
 * Add or edit a client project. Copied from AddActivityTypeDialog so the two master data
 * dialogs behave identically, including the MasterDataSelect client picker and the
 * parseApiError path that surfaces a duplicate-code conflict from the server.
 *
 * @since NEXT_VERSION
 */

const obj = {
  id: null,
  code: "",
  name: "",
  description: "",
  active: true,
  displayOrder: 0,
  clientId: null,
};

const AddClientProjectDialog = ({ open, setOpen, refreshProjectsHandler, editObject }) => {
  const dispatch = useDispatch();
  const [message, setMessage] = useState("");
  const [messageOpen, setMessageOpen] = useState(false);
  const [severity, setSeverity] = useState("info");
  const [data, setData] = useState(obj);
  const [fieldErrors, setFieldErrors] = useState({});
  // Guards the Active checkbox: unticking an existing project takes it out of service, so the
  // box stays ticked until this is confirmed. @since NEXT_VERSION
  const [confirmDeactivateOpen, setConfirmDeactivateOpen] = useState(false);
  const clients = useSelector((state) => state.client.rows);

  useEffect(() => {
    if (clients.length !== 0) return;
    loadAllClients()
      .then((response) => dispatch(clientActions.replaceData(response.data)))
      .catch((error) => {
        dispatch(clientActions.replaceData([]));
        handleAxiosError(error);
      });
  }, [clients.length, dispatch]);

  useEffect(() => {
    setMessage("");
    setMessageOpen(false);
    setSeverity("info");
    setConfirmDeactivateOpen(false);
    setFieldErrors({});
    if (!open) return;
    if (editObject) {
      setData({
        id: parseInt(editObject.id),
        code: editObject.code,
        name: editObject.name,
        description: editObject.description ?? "",
        active: editObject.active !== false,
        displayOrder: editObject.displayOrder ?? 0,
        clientId: editObject.client?.id ?? null,
      });
    } else {
      setData(obj);
    }
  }, [open, editObject]);

  const showBackendError = (error, fallback) => {
    const parsed = parseApiError(error);
    if (parsed.isUnauthorized) {
      handleAxiosError(error);
      return;
    }
    setErrorAction(parsed.message || fallback);
  };

  const saveProjectInBackend = async () => {
    // Each message is rendered against its own field, so nothing goes to the Alert below,
    // which is left for what the server reports.
    const payload = trimTextFields(data);
    const { isValid, fieldErrors: errs } = validateClientProject(payload);
    if (!isValid) {
      setFieldErrors(errs);
      return;
    }
    setFieldErrors({});
    if (editObject) await updateProjectInvoker(payload);
    else await addProjectInvoker(payload);
  };

  const addProjectInvoker = async (projectObject) => {
    await addClientProjectToBackend(projectObject)
      .then(() => {
        setSuccessAction("Project added successfully");
        closeAfterSuccess();
      })
      .catch((error) => showBackendError(error, "Failed to add project. Please try again."));
  };

  const updateProjectInvoker = async (projectObject) => {
    await updateClientProjectToBackend(projectObject.id, projectObject)
      .then(() => {
        setSuccessAction("Project updated successfully");
        closeAfterSuccess();
      })
      .catch((error) => showBackendError(error, "Failed to update project. Please try again."));
  };

  const closeAfterSuccess = () => {
    setTimeout(() => {
      setData(obj);
      setMessageOpen(false);
      setMessage("");
      setOpen(false);
      refreshProjectsHandler();
    }, 1000);
  };

  const setErrorAction = (errorMessage) => {
    setMessage(errorMessage);
    setMessageOpen(true);
    setSeverity("error");
  };

  // Drops one field's message as soon as it changes, so an error from the last submit does
  // not sit under a field that is now valid. @since NEXT_VERSION
  const clearFieldError = (field) =>
    setFieldErrors((prev) =>
      prev[field] === undefined ? prev : { ...prev, [field]: undefined }
    );

  const setSuccessAction = (successMessage) => {
    setMessage(successMessage);
    setMessageOpen(true);
    setSeverity("success");
  };

  return (
    <>
    <Dialog open={open} fullWidth={true} maxWidth="sm">
      <DialogTitle>{editObject ? `Update Project` : `Add New Project`}</DialogTitle>
      <DialogContent>
        <MDBox my={2}>
          {/* A project is never reassigned to another client, so the picker locks on edit. */}
          <MasterDataSelect
            id="client"
            label="Client"
            required
            placeholder="Select a client"
            options={clients}
            value={data?.clientId || ""}
            disabled={Boolean(editObject)}
            onChange={(e) => {
              setData({ ...data, clientId: e.target.value });
              clearFieldError("clientId");
            }}
            error={Boolean(fieldErrors.clientId)}
            helperText={fieldErrors.clientId}
          />
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="code"
            label="Code"
            required
            fullWidth
            variant="outlined"
            value={data?.code || ""}
            onBlur={trimOnBlur(setData, "code")}
            onChange={(e) => {
              setData({ ...data, code: e.target.value });
              clearFieldError("code");
            }}
            error={Boolean(fieldErrors.code)}
            helperText={fieldErrors.code}
            slotProps={{ htmlInput: { maxLength: AppConstants.MAX_PROJECT_CODE_LENGTH } }}
          />
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="name"
            label="Project Name"
            required
            fullWidth
            variant="outlined"
            value={data?.name || ""}
            onBlur={trimOnBlur(setData, "name")}
            onChange={(e) => {
              setData({ ...data, name: e.target.value });
              clearFieldError("name");
            }}
            error={Boolean(fieldErrors.name)}
            helperText={fieldErrors.name}
            slotProps={{ htmlInput: { maxLength: AppConstants.MAX_PROJECT_NAME_LENGTH } }}
          />
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="description"
            label="Description"
            fullWidth
            variant="outlined"
            value={data?.description || ""}
            onBlur={trimOnBlur(setData, "description")}
            onChange={(e) => {
              setData({ ...data, description: e.target.value });
              clearFieldError("description");
            }}
            error={Boolean(fieldErrors.description)}
            helperText={fieldErrors.description}
            slotProps={{ htmlInput: { maxLength: AppConstants.MAX_PROJECT_DESCRIPTION_LENGTH } }}
          />
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="displayOrder"
            label="Order"
            type="number"
            fullWidth
            variant="outlined"
            value={data?.displayOrder ?? 0}
            onChange={(e) => setData({ ...data, displayOrder: parseInt(e.target.value) })}
          />
        </MDBox>
        <MDBox my={2}>
          {/* Unchecking is the same as retiring: the project leaves the Add Activity dropdown
              but the activities already logged against it keep resolving its name. */}
          <FormControlLabel
            control={
              <Checkbox
                id="active"
                checked={data?.active !== false}
                onChange={(e) => {
                  // Only unticking an existing project needs confirming; ticking one back on,
                  // and setting a brand-new project inactive, deactivate nothing that is in use.
                  if (!e.target.checked && editObject) {
                    setConfirmDeactivateOpen(true);
                    return;
                  }
                  setData({ ...data, active: e.target.checked });
                }}
              />
            }
            label="Active"
          />
        </MDBox>
        <Collapse in={messageOpen}>
          <Alert
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setMessageOpen(false)}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
            severity={severity}
            sx={{ mb: 2 }}
          >
            {message}
          </Alert>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>Cancel</Button>
        <Button onClick={saveProjectInBackend} variant="contained">
          {editObject ? `Update` : `Add`}
        </Button>
      </DialogActions>
    </Dialog>
    <ConfirmDialog
      title={"Deactivate This Project?"}
      open={confirmDeactivateOpen}
      setOpen={setConfirmDeactivateOpen}
      onConfirm={() => setData((prev) => ({ ...prev, active: false }))}
      yesButtonLabel="Deactivate"
      noButtonLabel="Cancel"
    >
      {`Deactivate the project '${data?.code} - ${data?.name}'` +
        `${editObject?.client?.code ? ` for '${editObject.client.code}'` : ""}? ` +
        `Once you save, it will no longer be selectable when adding an activity. ` +
        `Activities already logged against it keep it in reports and exports.`}
    </ConfirmDialog>
    </>
  );
};

export default memo(AddClientProjectDialog);
